import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AllButton from '../AllButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import TierBadge from '@wix/da-ds/pkg/Badges/TierBadge';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import AllDeviants from '@wix/da-ds/pkg/Icons/24x24/AllDeviants';
import type { BIEventData } from '@wix/da-bi/pkg/types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import SelectionListItemDropdown from '@wix/da-shared-react/pkg/Dropdown/Preset/SelectionListItemDropdown';
import StandardDropdownToggle from '@wix/da-shared-react/pkg/Dropdown/Preset/StandardDropdown/StandardDropdownToggle';
import type { SubNavigation } from '../../../types/store';
import s from './SubscribedFilter.scss';

interface Props {
  filter: SubNavigation;
  size?: 'regular' | 'small';
  isActive?: boolean;
  hasNewContent?: boolean;
  biData?: BIEventData;
  className?: string;
}

const iconsBySubnav = {
  everyone: <AllDeviants size={IconSize.MEDIUM} disableDefaultColors />,
  subscribed: <TierBadge size={24} />,
};

const SubscribedFilter: React.FC<Props> = ({
  filter: { currentValue, options: subnavOptions = [] },
  isActive,
  hasNewContent,
  className,
  size = 'regular',
  biData,
}) => {
  const currentFilter = currentValue ?? 'everyone';
  const currentSubnav = subnavOptions.find(
    option => option.subnav === currentFilter
  );
  const logBiEvent = useBiLogger();
  const { t } = useTranslation();
  const [isClicked, setIsClicked] = useState(false);

  const items = useMemo(
    () =>
      subnavOptions.map(({ subnav }) => ({
        label: t(`deviantsBar.button.all.dropdown.${subnav}.label`),
        subLabel: t(`deviantsBar.button.all.dropdown.${subnav}.sublabel`),
        value: subnav,
        prefix: iconsBySubnav[subnav],
      })),
    [subnavOptions, t]
  );

  const onSelect = useCallback(
    value => {
      const selection = subnavOptions.find(option => option.subnav === value);
      if (selection?.url) {
        // only log if switching filters
        if (currentFilter !== value) {
          logBiEvent({
            evid: BiEventType.WATCH_SUB_FILTER_SELECT,
            filter_name: value === 'subscribed' ? 'subscribed' : 'everyone',
          });
        }
        window.location.assign(selection?.url);
      }
    },
    [subnavOptions, currentFilter, logBiEvent]
  );

  return (
    <div
      className={classnames(
        s['root'],
        hasNewContent && s['new'],
        isActive && s['active'],
        isClicked && s['clicked'],
        s[`size-${size}`],
        className
      )}
    >
      <PlainButtonOrLink
        href={currentSubnav?.url}
        biData={biData}
        onClick={() => setIsClicked(true)}
        className={s['selection-frame']}
      >
        <AllButton filterState={currentFilter} size={size} />
      </PlainButtonOrLink>
      <SelectionListItemDropdown
        items={items}
        selectedValue={currentFilter}
        onSelect={onSelect}
        isInPopup
        renderToggle={(id, selectedItem) => (
          <StandardDropdownToggle
            dropdownId={id}
            toggleClassName={s['label']}
            arrowSize={IconSize.TINY}
            arrowClassName={s['arrow']}
            selectedItem={selectedItem}
            placeholder={t(`deviantsBar.button.all.${currentFilter}`)}
            onOpenBiData={{
              evid: BiEventType.WATCH_SUB_FILTER_OPEN,
              filter_name:
                currentFilter === 'subscribed' ? currentFilter : 'everyone',
            }}
          />
        )}
      />
    </div>
  );
};
SubscribedFilter.displayName = 'SubscribedFilter';

export default SubscribedFilter;
